import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Loader2,
  LogOut,
  Lock,
  ArrowUpRight,
  ChevronLeft,
  CheckCircle2,
  AlertTriangle,
  ExternalLink,
  PlusCircle,
  Send,
} from 'lucide-react';
import PageShell from '@/components/PageShell';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'tmg_admin_token';

const verdictTone = (color) => ({
  red:    { ring: 'border-red-500/50',    bg: 'bg-red-500/10',    text: 'text-red-400' },
  amber:  { ring: 'border-amber/50',      bg: 'bg-amber/10',      text: 'text-amber' },
  green:  { ring: 'border-green-500/50',  bg: 'bg-green-500/10',  text: 'text-green-400' },
}[color] || { ring: 'border-white/20', bg: 'bg-white/5', text: 'text-white' });

const ScoreBar = ({ value, max = 10, tone }) => (
  <div className="flex items-center gap-3">
    <div className="flex-1 h-1.5 bg-white/5 relative overflow-hidden">
      <div className={`absolute inset-y-0 left-0 ${tone}`} style={{ width: `${(value / max) * 100}%` }} />
    </div>
    <span className="font-mono text-sm text-white tabular-nums">{value}/{max}</span>
  </div>
);

const ProspectCard = ({ p }) => {
  const tone = verdictTone(p.verdict_color);
  return (
    <article className="bg-ink-800 border border-white/10 hover:border-amber/30 transition-colors">
      {/* Header */}
      <div className="p-7 md:p-8 border-b border-white/10">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-cyan border border-cyan/30 px-2 py-1">
                {p.category}
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                {p.stage}
              </span>
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                · {p.geo}
              </span>
            </div>
            <h2 className="font-display text-3xl md:text-4xl text-white">{p.name}</h2>
          </div>
          <a
            href={p.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-amber hover:text-white transition-colors whitespace-nowrap"
          >
            Site <ExternalLink className="h-3 w-3" />
          </a>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-px bg-white/5">
        {/* What & Why & Fit */}
        <div className="lg:col-span-7 bg-ink-800 p-7 md:p-8 space-y-6">
          <div>
            <div className="overline text-amber mb-2">What they do</div>
            <p className="text-zinc-300 text-sm leading-relaxed">{p.what_they_do}</p>
          </div>
          <div>
            <div className="overline text-amber mb-2">Why interesting</div>
            <ul className="space-y-1.5 text-sm">
              {p.why_interesting.map((b) => (
                <li key={b} className="flex gap-2 text-zinc-300">
                  <span className="text-amber font-mono mt-1 shrink-0">+</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="overline text-amber mb-2">Strategic fit · value chain</div>
            <p className="text-zinc-400 text-sm leading-relaxed">{p.strategic_fit}</p>
          </div>
          <div>
            <div className="overline mb-2 text-red-400">Risks & red flags</div>
            <ul className="space-y-1.5 text-sm">
              {p.risks.map((r) => (
                <li key={r} className="flex gap-2 text-zinc-400">
                  <span className="text-red-400 font-mono mt-1 shrink-0">−</span>
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Capital · Access · Verdict */}
        <div className="lg:col-span-5 bg-ink-900 p-7 md:p-8 space-y-6">
          {/* Capital score */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="overline text-amber">Capital case</div>
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">Financial-returns view</span>
            </div>
            <ScoreBar value={p.capital.score} tone="bg-amber" />
            <ul className="mt-3 space-y-1 text-xs text-zinc-400 leading-relaxed">
              {p.capital.notes.map((n) => (
                <li key={n} className="flex gap-2"><span className="text-zinc-600">·</span>{n}</li>
              ))}
            </ul>
          </div>

          {/* Access score */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="overline text-cyan">Access case</div>
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">Strategic-utility view</span>
            </div>
            <ScoreBar value={p.access.score} tone="bg-cyan" />
            <ul className="mt-3 space-y-1 text-xs text-zinc-400 leading-relaxed">
              {p.access.notes.map((n) => (
                <li key={n} className="flex gap-2"><span className="text-zinc-600">·</span>{n}</li>
              ))}
            </ul>
          </div>

          {/* Verdict */}
          <div className={`border ${tone.ring} ${tone.bg} p-5`}>
            <div className="flex items-center gap-2 mb-3">
              {p.verdict_color === 'red' ? (
                <AlertTriangle className={`h-4 w-4 ${tone.text}`} />
              ) : (
                <CheckCircle2 className={`h-4 w-4 ${tone.text}`} />
              )}
              <span className="overline text-zinc-500">Verdict</span>
            </div>
            <div className={`font-display text-xl ${tone.text} mb-3`}>{p.verdict}</div>
            <p className="text-zinc-300 text-sm leading-relaxed">{p.verdict_reasoning}</p>
          </div>

          {/* What would change mind */}
          <div>
            <div className="overline mb-2">What would change our mind</div>
            <ul className="space-y-1.5 text-xs">
              {p.what_would_change_our_mind.map((it) => (
                <li key={it} className="flex gap-2 text-zinc-400">
                  <span className="text-amber font-mono shrink-0">→</span>
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </article>
  );
};

const FutureBets = () => {
  const [token, setToken] = useState(
    typeof window !== 'undefined' ? sessionStorage.getItem(STORAGE_KEY) || '' : '',
  );
  const [authed, setAuthed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  const load = async (tkn) => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API}/internal/prospects`, {
        headers: { 'X-Admin-Token': tkn },
      });
      setData(res.data);
      setAuthed(true);
      sessionStorage.setItem(STORAGE_KEY, tkn);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Invalid passcode');
      setAuthed(false);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signOut = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setToken('');
    setAuthed(false);
    setData(null);
  };

  if (!authed) {
    return (
      <PageShell>
        <section className="min-h-[70vh] grid place-items-center px-6 py-20">
          <form
            onSubmit={(e) => { e.preventDefault(); load(token); }}
            className="bg-ink-800 border border-amber/30 p-8 max-w-md w-full"
            data-testid="futurebets-gate-form"
          >
            <div className="grid h-12 w-12 place-items-center bg-amber/15 border border-amber mb-5">
              <Lock className="h-5 w-5 text-amber" />
            </div>
            <div className="overline text-amber mb-3">Internal · Future bets</div>
            <h1 className="font-display text-2xl md:text-3xl text-white mb-3">Investment pipeline</h1>
            <p className="text-zinc-400 text-sm leading-relaxed mb-6">
              This view contains unbiased internal thesis notes on prospects under
              consideration. It is gated to partners and reviewers only.
            </p>
            <label className="block mb-4">
              <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 block mb-2">
                Passcode
              </span>
              <input
                type="password"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="Enter passcode"
                data-testid="futurebets-token-input"
                className="w-full bg-ink-900 text-white placeholder:text-zinc-600 px-4 py-3 border border-white/10 outline-none focus:border-amber font-sans text-sm"
                autoFocus
                required
              />
            </label>
            {error && (
              <div className="mb-4 text-sm text-red-400 font-mono">{error}</div>
            )}
            <button
              type="submit"
              disabled={loading || !token}
              data-testid="futurebets-submit"
              className="w-full inline-flex items-center justify-center gap-2 border border-amber bg-amber px-6 py-3 text-xs font-mono uppercase tracking-[0.18em] text-black hover:bg-white hover:border-white transition-colors disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Unlock'}
            </button>

            <div className="mt-6">
              <Link
                to="/investments"
                className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 hover:text-amber transition-colors"
              >
                <ChevronLeft className="h-3 w-3" /> Back to investments
              </Link>
            </div>
          </form>
        </section>
      </PageShell>
    );
  }

  return (
    <PageShell>
      {/* HERO */}
      <section className="relative border-b border-white/10 overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-50" />
        <div className="absolute inset-0 radial-glow-amber opacity-30" />
        <div className="relative px-6 md:px-12 lg:px-16 pt-20 pb-12">
          <div className="flex items-center justify-between mb-6">
            <Link
              to="/investments"
              className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-400 hover:text-amber transition-colors"
            >
              <ChevronLeft className="h-3 w-3" /> Investments
            </Link>
            <button
              onClick={signOut}
              className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-400 hover:text-amber transition-colors"
            >
              <LogOut className="h-3 w-3" /> Sign out
            </button>
          </div>

          <div className="overline text-amber mb-4">
            <span className="inline-flex items-center gap-2">
              <Lock className="h-3 w-3" />
              Internal · Future bets pipeline · {data?.as_of}
            </span>
          </div>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-balance max-w-5xl leading-[1.02]">
            Capital vs access. <span className="text-amber">Two axes. One honest call.</span>
          </h1>
          <p className="mt-6 max-w-3xl text-zinc-300 text-base md:text-lg leading-relaxed">
            For each prospect we score two independent dimensions — the
            financial-returns case (capital) and the strategic-utility case
            (access) — and then take an unbiased view on whether to invest,
            pass, or watch.
          </p>

          {/* Framework */}
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-px bg-white/5 border border-white/10 max-w-4xl">
            <div className="bg-ink-900 p-5">
              <div className="overline text-amber mb-2">Capital</div>
              <p className="text-zinc-400 text-xs leading-relaxed">
                {data?.thesis_framework?.capital}
              </p>
            </div>
            <div className="bg-ink-900 p-5">
              <div className="overline text-cyan mb-2">Access</div>
              <p className="text-zinc-400 text-xs leading-relaxed">
                {data?.thesis_framework?.access}
              </p>
            </div>
            <div className="bg-ink-900 p-5">
              <div className="overline mb-2">Scoring</div>
              <p className="text-zinc-400 text-xs leading-relaxed">
                {data?.thesis_framework?.scoring}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* PROSPECTS */}
      <section className="px-6 md:px-12 lg:px-16 py-16 md:py-20 space-y-8">
        {data?.prospects?.map((p) => (
          <ProspectCard key={p.slug} p={p} />
        ))}
      </section>

      {/* SUBMIT A PROSPECT */}
      <SubmitProspect token={token} onSubmitted={() => load(token)} />

      {/* SUBMITTED PROSPECTS */}
      {data?.submitted?.length > 0 && (
        <section className="px-6 md:px-12 lg:px-16 pb-16">
          <div className="max-w-5xl">
            <div className="overline text-amber mb-3">From the partners' inbox</div>
            <h2 className="font-display text-2xl md:text-3xl mb-6">
              Prospects added by the team
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {data.submitted.map((s) => (
                <div key={s.id} className="bg-ink-800 border border-white/10 p-5 hover:border-amber/40 transition-colors">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="font-display text-lg text-white">{s.name}</div>
                    {s.url && (
                      <a
                        href={s.url.startsWith('http') ? s.url : `https://${s.url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-mono text-[10px] uppercase tracking-[0.18em] text-amber hover:text-white inline-flex items-center gap-1"
                      >
                        Visit <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                  {s.sector && (
                    <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-cyan mb-3">
                      {s.sector}
                    </div>
                  )}
                  <p className="text-zinc-300 text-sm leading-relaxed">{s.why}</p>
                  <div className="mt-3 font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-500">
                    {s.submitter_name ? `Added by ${s.submitter_name}` : 'Added internally'}
                    {' · '}{new Date(s.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* DISCLAIMER */}
      <section className="px-6 md:px-12 lg:px-16 pb-20">
        <div className="max-w-3xl font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500 leading-relaxed">
          Internal thesis notes · not investment advice · synthesised from
          public information &amp; founder conversations · scores are
          directional, not financial-grade.
        </div>
      </section>
    </PageShell>
  );
};

const SubmitProspect = ({ token, onSubmitted }) => {
  const [form, setForm] = useState({ name: '', url: '', sector: '', why: '', submitter_name: '', submitter_email: '' });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.submitter_email) delete payload.submitter_email;
      if (!payload.url) delete payload.url;
      if (!payload.sector) delete payload.sector;
      if (!payload.submitter_name) delete payload.submitter_name;
      await axios.post(`${API}/internal/prospects/submit`, payload, {
        headers: { 'X-Admin-Token': token },
      });
      setDone(true);
      setForm({ name: '', url: '', sector: '', why: '', submitter_name: '', submitter_email: '' });
      toast.success('Prospect added to the pipeline');
      if (onSubmitted) onSubmitted();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not submit — try again');
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <section className="px-6 md:px-12 lg:px-16 py-12 bg-black border-y border-white/10">
        <div className="max-w-3xl bg-ink-800 border border-amber/40 p-8">
          <div className="grid h-10 w-10 place-items-center bg-amber/15 border border-amber mb-4">
            <CheckCircle2 className="h-5 w-5 text-amber" />
          </div>
          <h3 className="font-display text-2xl text-white mb-2">Added to the pipeline.</h3>
          <p className="text-zinc-400 text-sm leading-relaxed mb-5">
            Thanks. We will look at it in the next review cycle. You can add another if you like.
          </p>
          <button
            type="button"
            onClick={() => setDone(false)}
            className="inline-flex items-center gap-2 border border-amber bg-amber px-5 py-2 text-xs font-mono uppercase tracking-[0.18em] text-black hover:bg-white hover:border-white transition-colors"
          >
            Add another <PlusCircle className="h-3.5 w-3.5" />
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="px-6 md:px-12 lg:px-16 py-12 bg-black border-y border-white/10">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-4">
          <div className="overline text-amber mb-3">
            <span className="inline-flex items-center gap-2">
              <PlusCircle className="h-3.5 w-3.5" /> Add a prospect
            </span>
          </div>
          <h2 className="font-display text-2xl md:text-3xl text-balance leading-tight">
            See a company worth looking at? <span className="text-amber">Drop it in here.</span>
          </h2>
          <p className="mt-4 text-zinc-400 text-sm leading-relaxed">
            Anything in the Indian automotive or mobility space — early or late, public or stealth.
            Two sentences on why it matters is enough.
          </p>
        </div>

        <form onSubmit={submit} className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
          <Field label="Company name" required>
            <input
              required
              type="text"
              value={form.name}
              onChange={update('name')}
              placeholder="e.g. Bolt.Earth"
              data-testid="submit-prospect-name"
              className={fldCls}
            />
          </Field>
          <Field label="Website">
            <input
              type="text"
              value={form.url}
              onChange={update('url')}
              placeholder="domain.com"
              className={fldCls}
            />
          </Field>
          <Field label="Sector / category" full>
            <input
              type="text"
              value={form.sector}
              onChange={update('sector')}
              placeholder="EV charging · CV telematics · used vehicles · workshop SaaS …"
              className={fldCls}
            />
          </Field>
          <Field label="Why interesting" required full>
            <textarea
              required
              rows={4}
              value={form.why}
              onChange={update('why')}
              placeholder="What's the wedge? Who are the founders? Why now?"
              className={`${fldCls} resize-y min-h-[110px]`}
            />
          </Field>
          <Field label="Your name">
            <input
              type="text"
              value={form.submitter_name}
              onChange={update('submitter_name')}
              placeholder="Optional"
              className={fldCls}
            />
          </Field>
          <Field label="Your email">
            <input
              type="email"
              value={form.submitter_email}
              onChange={update('submitter_email')}
              placeholder="Optional"
              className={fldCls}
            />
          </Field>
          <div className="md:col-span-2 bg-ink-900 px-5 py-4 flex items-center justify-between gap-4">
            <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-zinc-500">
              Only visible behind this passcode
            </p>
            <button
              type="submit"
              disabled={submitting}
              data-testid="submit-prospect-submit"
              className="inline-flex items-center gap-2 border border-amber bg-amber px-5 py-2.5 text-xs font-mono uppercase tracking-[0.18em] text-black hover:bg-white hover:border-white transition-colors disabled:opacity-60"
            >
              {submitting ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Sending</> : <>Submit <Send className="h-3.5 w-3.5" /></>}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

const fldCls = 'w-full bg-ink-900 text-white placeholder:text-zinc-600 px-4 py-3 border-0 outline-none focus:bg-black focus:ring-1 focus:ring-amber focus:ring-inset font-sans text-sm';
const Field = ({ label, children, required, full }) => (
  <label className={`block bg-ink-900 ${full ? 'md:col-span-2' : ''}`}>
    <div className="px-4 pt-4 pb-1 flex items-center justify-between">
      <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-zinc-500">{label}</span>
      {required && <span className="font-mono text-[10px] text-amber">required</span>}
    </div>
    {children}
  </label>
);

export default FutureBets;
